import { useQuery } from "@tanstack/react-query";
import type {
  HubspotPartnerDealRelationship,
  PartnerTier,
} from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { CollaboratorsList } from "./types";

// GET /api/admin/deals/:hubspotDealId
//
// Backs the deal-drawer payload — deal facts + attributions + cached
// account snapshot + collaborators + which attributed partners don't yet
// have portal access + the HubSpot deal owner. Returns 404 when the deal
// row isn't found.

export type Account = {
  id: string;
  hubspot_company_id: string;
  hubspot_url: string;
  name: string | null;
  domain: string | null;
  website: string | null;
  industry: string | null;
  description: string | null;
  location_city: string | null;
  location_country: string | null;
  employee_count: number | null;
  annual_revenue: number | null;
  last_synced_at: string | null;
};

export type AdminPartnerAttribution = {
  partner_id: string;
  partner_name: string;
  partner_tier: PartnerTier | null;
  partner_website: string | null;
  partner_manager_name: string | null;
  partner_hubspot_record_id: string | null;
  partner_hubspot_url: string | null;
  relationship: HubspotPartnerDealRelationship;
};

export type AdminUnboardedPartner = {
  partner_id: string;
  partner_name: string;
};

export type AdminHubspotDealOwner = {
  hubspotOwnerId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  archived?: boolean;
  matched: {
    userId: string;
    fullName: string | null;
    avatarUrl: string | null;
  } | null;
};

export type AdminDealDrawerPayload = {
  deal: {
    hubspot_deal_id: string;
    hubspot_url: string;
    deal_name: string | null;
    deal_amount: number | null;
    deal_currency_code: string | null;
    deal_close_date: string | null;
    deal_stage_id: string | null;
    deal_stage_label: string | null;
    deal_created_at: string | null;
    pulled_at: string | null;
  };
  attributions: AdminPartnerAttribution[];
  unboardedPartners: AdminUnboardedPartner[];
  account: Account | null;
  collaborators: CollaboratorsList;
  owner: AdminHubspotDealOwner | null;
};

export function useAdminDeal(hubspotDealId: string | null | undefined) {
  return useQuery<AdminDealDrawerPayload, ApiClientError>({
    queryKey: queryKeys.admin.deals.detail(hubspotDealId ?? ""),
    queryFn: () =>
      apiFetch<AdminDealDrawerPayload>(`/admin/deals/${hubspotDealId}`),
    enabled: !!hubspotDealId,
  });
}
